import { ColDef, ICellRendererParams } from '@ag-grid-community/core'
import { Badge, Button } from 'components/common'
import { CronJob, CronJobOwner, CronJobRow } from 'types/cronJob'
import { formatDate } from 'utils/helpers/date'
import { formatDuration, formatMinutes, HEALTH_INFO } from './utils'

type CellProps = ICellRendererParams<CronJobRow>

// Group header rows have no job data
const asJob = (row?: CronJobRow) => (row && !row.isGroupRow ? row : undefined)

const showDate = (date?: string) =>
  date ? formatDate(date, { includeTime: true }) : '-'

// e.g. "gag-cron#3 (server-1, pid 1234)"
const showOwner = (owner?: CronJobOwner) =>
  owner
    ? `${owner.appName}#${owner.pm2Id ?? '-'} (${owner.host}, pid ${owner.pid})`
    : '-'

// Two short lines; the full text is in the tooltip
const CompactCell = ({
  top,
  bottom,
  tooltip,
}: {
  top: React.ReactNode
  bottom?: React.ReactNode
  tooltip?: string
}) => (
  <div
    className="flex h-full flex-col justify-center leading-4"
    title={tooltip}
  >
    <div className="truncate">{top}</div>
    {bottom && (
      <div className="truncate text-[10px] text-gray-400">{bottom}</div>
    )}
  </div>
)

// Title with the job name; description in the tooltip
const JobCell = ({ data }: CellProps) => {
  const job = asJob(data)
  if (!job) return null

  return (
    <CompactCell
      top={<span className="font-medium">{job.title ?? job._id}</span>}
      bottom={job._id}
      tooltip={job.description}
    />
  )
}

// Expression, or "no schedule"; switched-off jobs are marked Off
const ScheduleCell = ({ data }: CellProps) => {
  const job = asJob(data)
  if (!job) return null

  const expression = job.expression ?? job.setupError?.expression

  return (
    <CompactCell
      top={
        <span className="flex items-center gap-1">
          <code className="text-xs">{expression ?? 'no schedule'}</code>
          {job.isActive === false && <Badge color="gray">Off</Badge>}
        </span>
      }
      bottom={expression && `max run ${formatMinutes(job.maxRunMinutes)}`}
    />
  )
}

// Fixed-width pills so every row lines up
const PILL = 'justify-center'

// Hidden while running: they describe earlier runs, which this run may fix
const QUIET_WHILE_RUNNING: CronJob['health'][] = [
  'healthy',
  'notRunYet',
  'failing',
  'overdue',
]

// Health pill and Running/Idle pill on one line, run time after them;
// why it is inactive below. The PM2 process is in the tooltip.
const StatusCell = ({ data }: CellProps) => {
  const job = asJob(data)
  if (!job) return null

  const isRunning = job.status === 'running'
  const reason =
    job.health === 'inactive'
      ? job.inactiveReason ?? 'Switched off on this page'
      : undefined

  const tooltip = [
    reason,
    isRunning
      ? `Running on ${showOwner(job.runningOn)}`
      : job.lastRunOn && `Last ran on ${showOwner(job.lastRunOn)}`,
  ]
    .filter(Boolean)
    .join('\n')

  // While running, "Healthy" / "Not run yet" add nothing: show only Running
  const showHealth = !isRunning || !QUIET_WHILE_RUNNING.includes(job.health)

  return (
    <div className="min-w-0" title={tooltip}>
      <div className="flex items-center gap-1.5">
        {showHealth && (
          <Badge
            color={HEALTH_INFO[job.health].color}
            className={`w-[92px] ${PILL}`}
          >
            {HEALTH_INFO[job.health].label}
          </Badge>
        )}
        <Badge
          color={isRunning ? 'blue' : 'gray'}
          className={`w-[60px] ${PILL}`}
        >
          {isRunning ? 'Running' : 'Idle'}
        </Badge>
        {isRunning && (
          <span className="text-[11px] text-gray-500">
            {formatMinutes(job.runningMinutes)}
          </span>
        )}
      </div>
      {/* Why it is inactive, on its own line under the pills */}
      {reason && (
        <p className="mt-1 truncate text-[10px] text-gray-500">{reason}</p>
      )}
    </div>
  )
}

// Last success in local time (the API sends UTC); run time below
const LastSuccessCell = ({ data }: CellProps) => {
  const job = asJob(data)
  if (!job) return null

  return (
    <CompactCell
      top={showDate(job.lastSuccessAt)}
      bottom={
        job.lastDurationMs !== undefined &&
        `took ${formatDuration(job.lastDurationMs)}`
      }
    />
  )
}

// Setup error, last error and failure/skip counts in one line
const ProblemsCell = ({ data }: CellProps) => {
  const job = asJob(data)
  if (!job) return null

  const problems = [
    job.setupError?.message,
    job.consecutiveFailures > 0 &&
      `${job.consecutiveFailures} failure(s): ${job.lastError ?? ''}`,
    job.consecutiveSkips > 0 && `${job.consecutiveSkips} skipped tick(s)`,
  ].filter(Boolean)

  if (!problems.length) return <span className="text-gray-400">-</span>

  const text = problems.join(' | ')

  return (
    <CompactCell
      top={<span className="text-red-600">{text}</span>}
      tooltip={text}
    />
  )
}

type OnJobAction = (job: CronJob) => void

// View/Edit always; Reset (editors) only while the job is marked running
const ActionCell = ({
  data,
  isEditor,
  onEdit,
  onReset,
}: CellProps & {
  isEditor: boolean
  onEdit: OnJobAction
  onReset: OnJobAction
}) => {
  const job = asJob(data)
  if (!job) return null

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="small"
        buttonText={isEditor ? 'Edit' : 'View'}
        className="!px-2 !py-0.5"
        onClick={() => onEdit(job)}
      />
      {isEditor && (
        <Button
          type="button"
          size="small"
          variant="outline"
          buttonText="Reset"
          className="!px-2 !py-0.5"
          disabled={job.status !== 'running'}
          onClick={() => onReset(job)}
        />
      )}
    </div>
  )
}

// The grid gives cells a row-high line-height; centre two-line cells instead
const CELL_CLASS = '!flex items-center !leading-4'

// Actions column pinned right; viewers get View, editors Edit and Reset
export const getCronJobColumns = (
  isEditor: boolean,
  onEdit: OnJobAction,
  onReset: OnJobAction,
): ColDef<CronJobRow>[] =>
  [
    { headerName: 'Job', colId: 'job', minWidth: 230, cellRenderer: JobCell },
    {
      headerName: 'Schedule',
      colId: 'schedule',
      minWidth: 150,
      cellRenderer: ScheduleCell,
    },
    {
      headerName: 'Status',
      colId: 'status',
      minWidth: 210,
      cellRenderer: StatusCell,
    },
    {
      headerName: 'Last Success',
      colId: 'lastSuccess',
      minWidth: 170,
      cellRenderer: LastSuccessCell,
    },
    {
      headerName: 'Problems',
      colId: 'problems',
      minWidth: 200,
      cellRenderer: ProblemsCell,
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      pinned: 'right' as const,
      minWidth: 140,
      maxWidth: 140,
      cellRenderer: (params: CellProps) => (
        <ActionCell
          {...params}
          isEditor={isEditor}
          onEdit={onEdit}
          onReset={onReset}
        />
      ),
    },
  ].map((column) => ({ ...column, cellClass: CELL_CLASS }))
